/**
 * The §13 states every list and panel must have: `Skeleton` (loading), `EmptyState`
 * (icon / title / description / CTA), `ErrorState` (retry + trace id for support), plus
 * `Notice` for inline informational/success/warning banners and `Spinner` for busy
 * affordances inside buttons or small regions.
 *
 * Icons: the illustration glyphs (empty, error, notice) are duotone; the spinner and the
 * retry button's glyph stay filled (rule at the top of icons.ts). Error and notice glyphs
 * sit in a semantic colour, so they take `DUOTONE_TINT` (second tone = a tint of that
 * colour) rather than the brand second tone.
 */
import { DUOTONE_TINT, Duotone, Loader2, RefreshCw } from './icons.ts';
import type { CSSProperties, ReactNode } from 'react';

import { Button } from './button.tsx';

export function Skeleton({
  width,
  height = 12,
  circle = false,
  className,
  style,
}: {
  width?: number | string | undefined;
  height?: number | string | undefined;
  circle?: boolean | undefined;
  className?: string | undefined;
  style?: CSSProperties | undefined;
}): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={['argus-skeleton', circle ? 'argus-skeleton--circle' : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
      style={{ width: width ?? '100%', height, ...style }}
    />
  );
}

/** `lines` stacked text skeletons, the last one shorter, with a polite status for AT. */
export function SkeletonText({
  lines = 3,
  label = 'Loading',
}: {
  lines?: number | undefined;
  label?: string | undefined;
}): JSX.Element {
  return (
    <div role="status" aria-label={label} className="stack-sm">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton
          key={i}
          className="argus-skeleton--text"
          width={i === lines - 1 ? '60%' : '100%'}
        />
      ))}
    </div>
  );
}

export function Spinner({
  size = 16,
  label = 'Loading',
}: {
  size?: number | undefined;
  label?: string | undefined;
}): JSX.Element {
  return (
    <span role="status" aria-label={label} className="argus-spinner" style={{ lineHeight: 0 }}>
      <Loader2 size={size} aria-hidden="true" />
    </span>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
  compact = false,
}: {
  icon?: ReactNode | undefined;
  title: ReactNode;
  description?: ReactNode | undefined;
  action?: ReactNode | undefined;
  compact?: boolean | undefined;
}): JSX.Element {
  return (
    <div
      className={['argus-empty', compact ? 'argus-empty--compact' : ''].filter(Boolean).join(' ')}
    >
      <span className="argus-empty__icon" aria-hidden="true">
        {icon ?? <Duotone.Inbox size={compact ? 18 : 22} />}
      </span>
      <p className="argus-empty__title">{title}</p>
      {description ? <p className="argus-empty__description">{description}</p> : null}
      {action ? <div className="argus-empty__action">{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  requestId,
  traceId,
  onRetry,
  retrying = false,
  actions,
}: {
  /** Defaults to "Something went wrong"; `null` renders NO title (F-31: message-only alert). */
  title?: ReactNode | null | undefined;
  message?: ReactNode | undefined;
  /**
   * F-37: the failed request's correlation id (`getRequestId(err)` from lib/request-id.ts),
   * shown as a small muted mono "Reference: <id>" line the user can copy for support (§13).
   */
  requestId?: string | null | undefined;
  /** Alias of `requestId` (the design page + older callers). `requestId` wins when both are set. */
  traceId?: string | null | undefined;
  onRetry?: (() => void) | undefined;
  retrying?: boolean | undefined;
  actions?: ReactNode | undefined;
}): JSX.Element {
  const reference = requestId ?? traceId;
  return (
    <div className="argus-error" role="alert">
      <Duotone.AlertTriangle
        className="argus-error__icon"
        size={18}
        aria-hidden="true"
        {...DUOTONE_TINT}
      />
      <div className="argus-error__body">
        {title !== null ? <p className="argus-error__title">{title}</p> : null}
        {message ? <p>{message}</p> : null}
        {reference ? (
          <p className="argus-error__trace">
            Reference: <code className="argus-error__reference">{reference}</code>
          </p>
        ) : null}
        {onRetry || actions ? (
          <div className="argus-error__actions">
            {onRetry ? (
              <Button
                size="sm"
                variant="secondary"
                loading={retrying}
                onClick={onRetry}
                icon={<RefreshCw size={14} aria-hidden="true" />}
              >
                Try again
              </Button>
            ) : null}
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
}

const NOTICE_ICON = {
  neutral: Duotone.Info,
  info: Duotone.Info,
  success: Duotone.CheckCircle2,
  warning: Duotone.AlertTriangle,
  /** F-31: a failure banner that is not a retryable `ErrorState` (styles in primitives-ext.css). */
  danger: Duotone.AlertTriangle,
} as const;

export function Notice({
  tone = 'neutral',
  children,
  role,
}: {
  tone?: keyof typeof NOTICE_ICON | undefined;
  children: ReactNode;
  role?: 'status' | 'alert' | 'note' | undefined;
}): JSX.Element {
  const Icon = NOTICE_ICON[tone];
  return (
    <div
      className={['argus-notice', tone !== 'neutral' ? `argus-notice--${tone}` : '']
        .filter(Boolean)
        .join(' ')}
      role={role ?? (tone === 'warning' || tone === 'danger' ? 'alert' : 'status')}
    >
      <Icon className="argus-notice__icon" size={16} aria-hidden="true" {...DUOTONE_TINT} />
      <div className="grow">{children}</div>
    </div>
  );
}
